/** Case workspace — the analyst's desk for one investigation. */
import { useState, useEffect, useCallback } from 'react';
import PhiFriendlyNote from '../../components/PhiFriendlyNote.jsx';
import {
  api, API, AiGlyph, Spinner, Badge, Hint, Tooltip, money, dateShort, dateTime,
  STAGE_LABEL, STAGE_TONE, CHANNEL_LABEL, CATEGORY_LABEL, PRIORITY_TONE, SUBJECT_TYPE_LABEL,
} from './ui';
import SentinelRegulatoryPanel from './SentinelRegulatoryPanel';

const TOOL_LABEL = {
  get_referral_facts: 'referral facts', get_subject_history: 'subject history',
  get_similar_cases: 'similar cases', get_case_timeline: 'timeline',
};
const DISPO_LABEL = {
  confirmed: 'Investigating', monitor: 'Monitoring', not_a_case: 'Not a case',
  substantiated: 'Substantiated', partially_substantiated: 'Partially substantiated',
  unsubstantiated: 'Unsubstantiated', unable_to_determine: 'Unable to determine',
};

export default function CaseWorkspace({ caseId, onBack, user }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [draftModal, setDraftModal] = useState(null); // { title, draft }

  const load = useCallback(() => {
    api(`/cases/${caseId}`)
      .then(setData)
      .catch((e) => setErr(e.message))
      .finally(() => setLoading(false));
  }, [caseId]);
  useEffect(() => { load(); }, [load]);

  if (loading) return <Spinner label="Opening the case…" />;
  if (err) return <div className="p-6 text-sm text-red-600">{err} <button onClick={onBack} className="underline">Go back</button></div>;
  if (!data) return null;

  const c = data.case;
  const undecided = !c.disposition;
  const notACase = c.disposition === 'not_a_case';
  const monitoring = c.disposition === 'monitor';
  const investigating = !undecided && !notACase && !monitoring;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <button onClick={onBack} className="text-sm text-slate-500 hover:text-slate-800">← All cases</button>

      <div className="lg:flex gap-5 mt-3 items-start">
        {/* Main column */}
        <div className="flex-1 min-w-0 space-y-5">
          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            <div className="flex items-start gap-3 flex-wrap">
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xs text-slate-400">{c.case_number}</span>
                  <Badge tone={STAGE_TONE[c.stage] || 'slate'}>{STAGE_LABEL[c.stage] || c.stage}</Badge>
                  <Badge tone={PRIORITY_TONE[c.priority] || 'slate'}>{c.priority} priority</Badge>
                  {c.exclusion_flag && (
                    <Tooltip text="This subject appears on a federal exclusion list (from your FDR module). That's a strong fraud signal — treat with elevated severity.">
                      <Badge tone="red">On exclusion list</Badge>
                    </Tooltip>
                  )}
                </div>
                <h1 className="text-lg font-semibold text-slate-900 mt-2 max-w-2xl">{c.allegation_summary || '(no summary provided)'}</h1>
                <p className="text-sm text-slate-500 mt-1">
                  {CATEGORY_LABEL[c.allegation_category] || 'Scheme not yet classified'} · arrived via {CHANNEL_LABEL[c.source_channel] || c.source_channel} · {dateShort(c.created_at)}
                </p>
              </div>
              <div className="ml-auto text-right">
                <div className="text-xs text-slate-400">Estimated $ at risk</div>
                <div className="text-xl font-bold text-slate-800">{money(c.estimated_exposure)}</div>
              </div>
            </div>

            <SubjectStrip subject={data.subject} caseRow={c} onUnmask={load} caseId={caseId} />
            <SubjectMemoryStrip memory={data.subject_memory} />
          </div>

          {/* Disposition — the key decision (only while undecided) */}
          {undecided && <DispositionPanel caseId={caseId} onChanged={load} setDraftModal={setDraftModal} />}

          {/* Monitoring watchlist outcome */}
          {monitoring && <MonitoringPanel caseId={caseId} caseRow={c} onChanged={load} />}

          {/* Not-a-case outcome (education) */}
          {notACase && <NotACasePanel caseId={caseId} caseRow={c} setDraftModal={setDraftModal} />}

          {/* Investigation tools (real cases) */}
          {investigating && (
            <>
              <AIPanel caseId={caseId} setDraftModal={setDraftModal} />
              <RecordsPanel caseId={caseId} requests={data.record_requests} onChanged={load} setDraftModal={setDraftModal} />
              <EvidencePanel caseId={caseId} evidence={data.evidence} onChanged={load} setDraftModal={setDraftModal} />
              <OverpaymentPanel caseId={caseId} overpayments={data.overpayments} onChanged={load} />
              <ReferralPanel caseId={caseId} referrals={data.referrals} onChanged={load} />
              {c.stage !== 'closed' && <ResolvePanel caseId={caseId} onChanged={load} />}
            </>
          )}

          {/* Regulatory reporting — line of business + state report fields */}
          <SentinelRegulatoryPanel caseId={caseId} caseRow={c} onChanged={load} />

          {/* Timeline — always visible */}
          <Timeline caseId={caseId} events={data.events} onChanged={load} />
        </div>

        {/* Sticky status rail — at-a-glance case state */}
        <StatusRail c={c} data={data} caseId={caseId} onChanged={load} user={user} />
      </div>

      {draftModal && <DraftModal {...draftModal} onClose={() => setDraftModal(null)} />}
    </div>
  );
}

/* ----------------------------------------------------------------------- */
// At-a-glance status rail — the analyst never has to scroll to know where a
// case stands, what it's waiting on, or when the 60-day clock runs out.
function StatusRail({ c, data, caseId, onChanged, user }) {
  const openOver = (data.overpayments || [])
    .filter((o) => !['recovered', 'reported', 'written_off'].includes(o.status) && o.report_due_at)
    .sort((a, b) => new Date(a.report_due_at) - new Date(b.report_due_at));
  const soonest = openOver[0];
  const dueDays = soonest ? Math.ceil((new Date(soonest.report_due_at) - new Date()) / 86400000) : null;
  const dueTone = dueDays == null ? 'slate' : dueDays <= 7 ? 'red' : dueDays <= 21 ? 'amber' : 'slate';
  const Row = ({ label, children }) => (
    <div className="flex items-center justify-between gap-3 py-2 border-b border-slate-100 last:border-0">
      <span className="text-xs text-slate-400">{label}</span>
      <span className="text-sm text-slate-700 text-right">{children}</span>
    </div>
  );
  return (
    <aside className="hidden lg:block w-[290px] flex-shrink-0 sticky top-6 self-start">
      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Case status</div>
        <Row label="Stage"><Badge tone={STAGE_TONE[c.stage] || 'slate'}>{STAGE_LABEL[c.stage] || c.stage}</Badge></Row>
        <Row label="Disposition">{c.disposition ? (DISPO_LABEL[c.disposition] || c.disposition) : <span className="text-slate-400">Awaiting decision</span>}</Row>
        <Row label="Priority"><Badge tone={PRIORITY_TONE[c.priority] || 'slate'}>{c.priority}</Badge></Row>
        {c.disposition === 'monitor' && c.monitor_review_date && <Row label="Review by">{dateShort(c.monitor_review_date)}</Row>}
        {soonest && <Row label="60-day clock"><Badge tone={dueTone}>{dueDays < 0 ? `${Math.abs(dueDays)}d over` : `${dueDays}d left`}</Badge></Row>}
        <Row label="$ at risk">{money(c.estimated_exposure)}</Row>
        <Row label="Opened">{dateShort(c.opened_at || c.created_at)}</Row>
        <div className="pt-2"><AssignControl c={c} caseId={caseId} onChanged={onChanged} user={user} /></div>
      </div>
    </aside>
  );
}

/* ----------------------------------------------------------------------- */
// Assign a case owner — "Assign to me", pick someone, or unassign.
function AssignControl({ c, caseId, onChanged, user }) {
  const [open, setOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState(false);
  const loadUsers = () => { if (!users.length) api('/users').then((j) => setUsers(j.users || [])).catch(() => {}); };
  const assign = async (uid) => {
    setBusy(true);
    try { await api(`/cases/${caseId}`, { method: 'PATCH', body: { assigned_to_user_id: uid } }); setOpen(false); onChanged && onChanged(); }
    catch (e) { alert(e.message); } finally { setBusy(false); }
  };
  return (
    <div>
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs text-slate-400">Owner</span>
        <button onClick={() => { setOpen(!open); loadUsers(); }} className="text-xs text-red-600 hover:text-red-700 font-medium">{open ? 'Cancel' : 'Assign'}</button>
      </div>
      <div className="text-sm text-slate-700 mt-0.5">{c.assigned_to_name || <span className="text-slate-400">Unassigned</span>}</div>
      {open && (
        <div className="mt-2 space-y-1.5">
          {user?.id && <button onClick={() => assign(user.id)} disabled={busy} className="w-full text-left text-xs px-2 py-1.5 rounded-lg bg-red-50 text-red-700 font-medium hover:bg-red-100 disabled:opacity-50">Assign to me</button>}
          <select disabled={busy} onChange={(e) => e.target.value && assign(e.target.value)} defaultValue="" className="w-full text-xs px-2 py-1.5 rounded-lg border border-slate-200 bg-white">
            <option value="">Assign to someone…</option>
            {users.map((u) => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
          </select>
          {c.assigned_to_user_id && <button onClick={() => assign(null)} disabled={busy} className="w-full text-left text-xs px-2 py-1.5 rounded-lg text-slate-500 hover:bg-slate-50 disabled:opacity-50">Unassign</button>}
        </div>
      )}
    </div>
  );
}

/* ----------------------------------------------------------------------- */
function SubjectStrip({ subject, caseRow, onUnmask, caseId }) {
  const [revealed, setRevealed] = useState(null);
  const unmask = async () => {
    const reason = window.prompt('Why are you revealing the reporter (logged for audit)?') || '';
    try { const r = await api(`/cases/${caseId}/unmask-reporter`, { method: 'POST', body: { reason } }); setRevealed(r); } catch (e) { alert(e.message); }
  };
  return (
    <div className="mt-4 pt-4 border-t border-slate-100 grid sm:grid-cols-2 gap-4 text-sm">
      <div>
        <div className="text-xs text-slate-400 mb-1">Subject of the case</div>
        {subject ? (
          <div>
            <span className="font-medium text-slate-800">{subject.display_name}</span>
            <span className="text-slate-400"> · {SUBJECT_TYPE_LABEL[subject.subject_type] || subject.subject_type}</span>
            {subject.npi && <div className="text-xs text-slate-500 font-mono mt-0.5">NPI {subject.npi}</div>}
          </div>
        ) : <span className="text-slate-400">No subject linked</span>}
      </div>
      <div>
        <div className="text-xs text-slate-400 mb-1 flex items-center">Reporter<Hint text="Who reported this. Anonymous reporters are protected — their identity is masked and only revealed with an audited click (non-retaliation)." /></div>
        {caseRow.reporter_anonymous ? (
          revealed ? (
            <span className="text-slate-700">{revealed.reporter_name || '(name not on file)'} {revealed.reporter_contact ? `· ${revealed.reporter_contact}` : ''}</span>
          ) : (
            <span className="text-slate-500">Anonymous · <button onClick={unmask} className="text-red-600 hover:underline">reveal (audited)</button></span>
          )
        ) : (
          <span className="text-slate-700">{caseRow.reporter_name || 'Not recorded'}</span>
        )}
      </div>
    </div>
  );
}

/* ----------------------------------------------------------------------- */
function DispositionPanel({ caseId, onChanged, setDraftModal }) {
  const [rec, setRec] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState(null); // null | 'monitor' | 'not_a_case'
  const [reviewDate, setReviewDate] = useState('');
  const [monitorReason, setMonitorReason] = useState('');
  const [education, setEducation] = useState('coach_referrer');

  const runTriage = async () => {
    setBusy(true);
    try {
      const { recommendation } = await api(`/cases/${caseId}/agents/triage`, { method: 'POST' });
      setRec(recommendation);
      if (recommendation?.monitor_reason) setMonitorReason(recommendation.monitor_reason);
      if (recommendation?.recommended_education && recommendation.recommended_education !== 'none') setEducation(recommendation.recommended_education);
    } catch (e) { alert(e.message); } finally { setBusy(false); }
  };

  const decide = async (decision, extra = {}) => {
    let note = null;
    // Choosing differently than the AI? Ask why — it teaches the Triage agent (PSI ▸ Agents).
    if (rec && rec.recommended_disposition && rec.recommended_disposition !== decision) {
      note = window.prompt("You're choosing differently than the AI recommended. Tell it why — this teaches the Triage agent:") || '';
    }
    setSaving(true);
    try {
      await api(`/cases/${caseId}/disposition`, { method: 'POST', body: { decision, priority: rec?.priority, allegation_category: rec?.allegation_category, note, ...extra } });
      onChanged();
    } catch (e) { alert(e.message); } finally { setSaving(false); }
  };

  const recLabel = rec && ({ investigate: 'Investigate', monitor: 'Monitor', not_a_case: 'Not a case' }[rec.recommended_disposition] || rec.recommended_disposition);
  const recTone = rec?.recommended_disposition === 'investigate' ? 'red' : rec?.recommended_disposition === 'monitor' ? 'amber' : 'slate';

  return (
    <div className="bg-white rounded-2xl border-2 border-red-100 p-5">
      <div className="flex items-center">
        <h2 className="font-semibold text-slate-800 flex items-center">What should happen with this referral?<Hint text="Your first decision. Investigate opens a case. Monitor keeps it on a watchlist to revisit later. Not a case closes it — and lets you educate the referrer or the provider." /></h2>
        <Tooltip text="Let the AI triage assistant weigh the facts and recommend a disposition, with its reasoning. You always make the final call.">
          <button onClick={runTriage} disabled={busy} className="ml-auto px-3 py-1.5 rounded-lg bg-slate-900 text-white text-sm font-medium disabled:opacity-40 hover:bg-slate-800 inline-flex items-center gap-2">
            {busy ? <span className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" /> : <AiGlyph className="w-3.5 h-3.5" />}
            {busy ? 'Thinking…' : 'Ask AI to triage'}
          </button>
        </Tooltip>
      </div>

      {rec && (
        <div className="mt-4 rounded-xl bg-slate-50 border border-slate-100 p-4 text-sm space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge tone={recTone}>AI suggests: {recLabel}</Badge>
            {rec.priority && <Badge tone={PRIORITY_TONE[rec.priority]}>{rec.priority} priority</Badge>}
            {typeof rec.triage_score === 'number' && <span className="text-xs text-slate-400">score {rec.triage_score}/100</span>}
          </div>
          <p className="text-slate-700">{rec.reasoning}</p>
          {rec.cited_facts?.length > 0 && (
            <div className="text-xs text-slate-500"><span className="font-medium">Based on:</span> {rec.cited_facts.join(' · ')}</div>
          )}
          {rec.suggested_next_steps?.length > 0 && (
            <div className="text-xs text-slate-600"><span className="font-medium">Suggested next steps:</span>
              <ul className="list-disc ml-5 mt-1">{rec.suggested_next_steps.map((s, i) => <li key={i}>{s}</li>)}</ul>
            </div>
          )}
          {rec._agent && (
            <p className="text-[11px] text-slate-400">
              Agent ran {rec._agent.steps} step{rec._agent.steps === 1 ? '' : 's'}
              {rec._agent.tools_used?.length > 0 && <> · pulled {[...new Set(rec._agent.tools_used)].map((t) => TOOL_LABEL[t] || t).join(', ')}</>}.
            </p>
          )}
          <p className="text-[11px] text-slate-400 italic">A recommendation only — you decide.</p>
        </div>
      )}

      {/* The three choices */}
      <div className="mt-4 flex flex-wrap gap-3">
        <button onClick={() => decide('investigate')} disabled={saving}
          className="px-4 py-2 rounded-xl bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-50">
          Investigate — open a case
        </button>
        <button onClick={() => setMode(mode === 'monitor' ? null : 'monitor')} disabled={saving}
          className={`px-4 py-2 rounded-xl text-sm font-semibold border ${mode === 'monitor' ? 'bg-amber-100 border-amber-300 text-amber-800' : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-50'}`}>
          Monitor — watch for now
        </button>
        <button onClick={() => setMode(mode === 'not_a_case' ? null : 'not_a_case')} disabled={saving}
          className={`px-4 py-2 rounded-xl text-sm font-semibold border ${mode === 'not_a_case' ? 'bg-slate-200 border-slate-300 text-slate-800' : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-50'}`}>
          Not a real FWA case
        </button>
      </div>

      {/* Monitor sub-form */}
      {mode === 'monitor' && (
        <div className="mt-3 rounded-xl bg-amber-50 border border-amber-100 p-4 space-y-2">
          <div className="text-sm font-medium text-amber-900">Put on the monitoring watchlist</div>
          <p className="text-xs text-amber-800">Not opening a case now — Sentinel keeps it on a watchlist so the team can revisit it. You can promote it to a full investigation anytime.</p>
          <div className="grid sm:grid-cols-2 gap-2">
            <label className="text-xs text-amber-900">Review by
              <input type="date" value={reviewDate} onChange={(e) => setReviewDate(e.target.value)} className="w-full mt-1 px-3 py-2 rounded-lg border border-amber-200 text-sm bg-white" />
            </label>
            <label className="text-xs text-amber-900">What to watch for
              <input value={monitorReason} onChange={(e) => setMonitorReason(e.target.value)} placeholder="e.g. repeat of the same billing pattern" className="w-full mt-1 px-3 py-2 rounded-lg border border-amber-200 text-sm bg-white" />
            </label>
          </div>
          <button onClick={() => decide('monitor', { monitor_review_date: reviewDate || null, monitor_reason: monitorReason || null })} disabled={saving}
            className="px-4 py-2 rounded-xl bg-amber-600 text-white text-sm font-semibold hover:bg-amber-700 disabled:opacity-50">Add to watchlist</button>
        </div>
      )}

      {/* Not-a-case sub-form — education is front and center */}
      {mode === 'not_a_case' && (
        <div className="mt-3 rounded-xl bg-slate-50 border border-slate-200 p-4 space-y-2">
          <div className="text-sm font-medium text-slate-800">Close as not a case — educate?</div>
          <p className="text-xs text-slate-500">It wasn't fraud, waste, or abuse. Choose whether to educate so the next referral or claim is better. You'll draft the note after closing.</p>
          <div className="space-y-1.5">
            {[
              ['coach_referrer', 'Coach the referrer', 'A friendly note to whoever reported it — what to look for next time.'],
              ['educate_provider', 'Educate the provider', 'A professional note about a minor, non-FWA billing issue to correct going forward.'],
              ['none', 'No education needed', 'Just close it.'],
            ].map(([v, label, desc]) => (
              <label key={v} className="flex items-start gap-2 text-sm cursor-pointer">
                <input type="radio" name="edu" checked={education === v} onChange={() => setEducation(v)} className="mt-1" />
                <span><span className="font-medium text-slate-700">{label}</span><span className="text-xs text-slate-500 block">{desc}</span></span>
              </label>
            ))}
          </div>
          <button onClick={() => decide('not_a_case', { education })} disabled={saving}
            className="px-4 py-2 rounded-xl bg-slate-800 text-white text-sm font-semibold hover:bg-slate-900 disabled:opacity-50">Close case</button>
        </div>
      )}
    </div>
  );
}

/* ----------------------------------------------------------------------- */
// Monitoring watchlist — a case being watched rather than investigated.
function MonitoringPanel({ caseId, caseRow, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reason, setReason] = useState(caseRow.monitor_reason || '');
  const [reviewDate, setReviewDate] = useState(caseRow.monitor_review_date ? String(caseRow.monitor_review_date).slice(0, 10) : '');

  const startEdit = () => {
    setReason(caseRow.monitor_reason || '');
    setReviewDate(caseRow.monitor_review_date ? String(caseRow.monitor_review_date).slice(0, 10) : '');
    setEditing(true);
  };
  const save = async () => {
    setSaving(true);
    try {
      await api(`/cases/${caseId}/monitor`, { method: 'PATCH', body: { monitor_reason: reason || null, monitor_review_date: reviewDate || null } });
      setEditing(false); onChanged();
    } catch (e) { alert(e.message); } finally { setSaving(false); }
  };
  const promote = async () => {
    if (!window.confirm('Promote this to a full investigation?')) return;
    setBusy(true);
    try { await api(`/cases/${caseId}/disposition`, { method: 'POST', body: { decision: 'investigate', priority: caseRow.priority } }); onChanged(); }
    catch (e) { alert(e.message); } finally { setBusy(false); }
  };

  return (
    <div className="bg-white rounded-2xl border border-amber-200 p-5">
      <div className="flex items-center">
        <h2 className="font-semibold text-slate-800 flex items-center">On the monitoring watchlist<Hint text="This referral is being watched rather than actively investigated. Update what you're watching for or the review date as things develop, add notes below, then promote it to a case or close it." /></h2>
        <button onClick={() => (editing ? setEditing(false) : startEdit())} className="ml-auto text-sm text-amber-700 font-medium hover:text-amber-800">{editing ? 'Cancel' : 'Update'}</button>
      </div>

      {!editing ? (
        <div className="mt-2 text-sm text-slate-600 space-y-1">
          {caseRow.monitor_reason && <p><span className="text-slate-400">Watching for:</span> {caseRow.monitor_reason}</p>}
          {caseRow.monitor_review_date && <p><span className="text-slate-400">Review by:</span> {dateShort(caseRow.monitor_review_date)}</p>}
          {!caseRow.monitor_reason && !caseRow.monitor_review_date && <p className="text-slate-400">No rationale or review date set yet — use Update to add them.</p>}
        </div>
      ) : (
        <div className="mt-3 grid sm:grid-cols-2 gap-2">
          <label className="text-xs text-slate-600 sm:col-span-2">What to watch for
            <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. repeat of the same billing pattern, or volume crossing a threshold" className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-amber-200" />
          </label>
          <label className="text-xs text-slate-600">Review by
            <input type="date" value={reviewDate} onChange={(e) => setReviewDate(e.target.value)} className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 text-sm bg-white" />
          </label>
          <div className="sm:col-span-2">
            <button onClick={save} disabled={saving} className="px-4 py-2 rounded-xl bg-amber-600 text-white text-sm font-semibold hover:bg-amber-700 disabled:opacity-50">{saving ? 'Saving…' : 'Save changes'}</button>
          </div>
        </div>
      )}

      <button onClick={promote} disabled={busy} className="mt-3 px-4 py-2 rounded-xl bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-50">
        {busy ? 'Promoting…' : 'Promote to investigation'}
      </button>
    </div>
  );
}

/* ----------------------------------------------------------------------- */
// Not a case — close the loop with education (referrer and/or provider).
function NotACasePanel({ caseId, caseRow, setDraftModal }) {
  const [busy, setBusy] = useState('');
  const draftDoc = async (docType, title) => {
    setBusy(docType);
    try {
      const { draft } = await api(`/cases/${caseId}/agents/draft`, { method: 'POST', body: { docType } });
      setDraftModal({ title, draft });
    } catch (e) { alert(e.message); } finally { setBusy(''); }
  };
  const outcomes = caseRow.outcomes || [];
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5">
      <h2 className="font-semibold text-slate-800 flex items-center">Closed — not a case<Hint text="This referral didn't meet the bar for an investigation. Educate where it helps: coach the referrer, or send the provider a light-touch education note." /></h2>
      <p className="text-sm text-slate-500 mt-1">Draft an education note to close the loop. Review and edit it before sending.</p>
      <div className="mt-3 flex flex-wrap gap-2">
        <button onClick={() => draftDoc('coaching_note', 'Coaching note for the referrer')} disabled={busy} className="px-3 py-1.5 rounded-lg bg-slate-900 text-white text-sm font-medium disabled:opacity-40 hover:bg-slate-800 inline-flex items-center gap-2">
          {busy === 'coaching_note' ? <span className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" /> : <AiGlyph className="w-3.5 h-3.5" />}
          Coach the referrer
          {outcomes.includes('coaching') && <span className="text-[10px] bg-white/20 px-1.5 rounded">suggested</span>}
        </button>
        <button onClick={() => draftDoc('provider_education', 'Provider education letter')} disabled={busy} className="px-3 py-1.5 rounded-lg bg-white border border-slate-300 text-slate-700 text-sm font-medium disabled:opacity-40 hover:bg-slate-50 inline-flex items-center gap-2">
          {busy === 'provider_education' ? <span className="w-3.5 h-3.5 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin" /> : <AiGlyph className="w-3.5 h-3.5" />}
          Educate the provider
          {outcomes.includes('provider_education') && <span className="text-[10px] bg-slate-100 px-1.5 rounded">suggested</span>}
        </button>
      </div>
    </div>
  );
}

/* ----------------------------------------------------------------------- */
function AIPanel({ caseId, setDraftModal }) {
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState('');
  const investigate = async () => {
    setBusy('investigate');
    try { const { plan } = await api(`/cases/${caseId}/agents/investigate`, { method: 'POST' }); setPlan(plan); }
    catch (e) { alert(e.message); } finally { setBusy(''); }
  };
  const draft = async (docType, title) => {
    setBusy(docType);
    try { const { draft } = await api(`/cases/${caseId}/agents/draft`, { method: 'POST', body: { docType } }); setDraftModal({ title, draft }); }
    catch (e) { alert(e.message); } finally { setBusy(''); }
  };
  // Feedback on the investigator's plan — teaches the agent (PSI ▸ Agents).
  const [sent, setSent] = useState(false);
  const sendFeedback = async (verdict) => {
    let reason = '';
    if (verdict === 'needs_work') reason = window.prompt('What should it do better? This teaches the Investigator agent:') || '';
    try {
      await api(`/cases/${caseId}/agents/feedback`, { method: 'POST', body: { agent: 'sentinel_investigator', field: 'plan_feedback', original_value: 'ai_plan', corrected_value: verdict, reason } });
      setSent(true);
    } catch (e) { alert(e.message); }
  };
  const btn = 'px-3 py-1.5 rounded-lg text-sm font-medium disabled:opacity-40 inline-flex items-center gap-2';
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5">
      <h2 className="font-semibold text-slate-800 flex items-center">AI investigation assistant<Hint text="Your tireless junior investigator. It drafts findings and a plan, and writes letters — every suggestion is yours to edit. It never sends anything or touches money on its own." /></h2>
      <div className="mt-3 flex flex-wrap gap-2">
        <button onClick={investigate} disabled={busy} className={btn + ' bg-slate-900 text-white hover:bg-slate-800'}>
          {busy === 'investigate' ? <span className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" /> : <AiGlyph className="w-3.5 h-3.5" />}
          Draft findings &amp; a plan
        </button>
        <Tooltip text="A professional letter to the provider raising the billing concern and the correct expectation."><button onClick={() => draft('provider_education', 'Provider education letter')} disabled={busy} className={btn + ' bg-white border border-slate-300 text-slate-700 hover:bg-slate-50'}><AiGlyph className="w-3.5 h-3.5" />Provider education letter</button></Tooltip>
        <Tooltip text="The 'Description of Findings/Allegations' narrative for an I-MEDIC referral — the suspected scheme, dates, dollars, and codes."><button onClick={() => draft('imedic_narrative', 'I-MEDIC referral narrative')} disabled={busy} className={btn + ' bg-white border border-slate-300 text-slate-700 hover:bg-slate-50'}><AiGlyph className="w-3.5 h-3.5" />I-MEDIC narrative</button></Tooltip>
      </div>

      {plan && (
        <div className="mt-4 rounded-xl bg-slate-50 border border-slate-100 p-4 text-sm space-y-3">
          <div><div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Findings</div><p className="text-slate-700 mt-1">{plan.findings_summary}</p></div>
          {plan.investigation_plan?.length > 0 && (
            <div><div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Investigation plan</div>
              <ul className="list-disc ml-5 mt-1 text-slate-700">{plan.investigation_plan.map((s, i) => <li key={i}>{s}</li>)}</ul></div>
          )}
          {plan.records_needed?.length > 0 && (
            <div><div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Records to request</div>
              <ul className="list-disc ml-5 mt-1 text-slate-700">{plan.records_needed.map((s, i) => <li key={i}>{s}</li>)}</ul>
              <p className="text-[11px] text-slate-400 italic mt-1">Remember: check internally (Utilization Management, then Risk Adjustment) before going to the provider.</p></div>
          )}
          {plan.clinical_review_needed && (
            <div className="rounded-lg bg-amber-50 border border-amber-100 p-3">
              <div className="text-xs font-semibold text-amber-700">Clinical review recommended</div>
              <p className="text-slate-700 mt-1">{plan.clinical_question}</p>
              <p className="text-[11px] text-slate-400 italic mt-1">Route to a nurse/MD for clinical review.</p>
            </div>
          )}
          <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
            {sent ? <span className="text-xs text-emerald-600">Thanks — the Investigator agent will learn from this.</span> : (
              <>
                <span className="text-xs text-slate-400 flex items-center">Was this helpful?<Hint text="Your rating teaches the Investigator agent and appears in PSI ▸ Agents." /></span>
                <button onClick={() => sendFeedback('helpful')} className="px-2 py-1 rounded-lg bg-emerald-50 text-emerald-700 text-xs font-medium hover:bg-emerald-100">Helpful</button>
                <button onClick={() => sendFeedback('needs_work')} className="px-2 py-1 rounded-lg bg-slate-100 text-slate-600 text-xs font-medium hover:bg-slate-200">Needs work</button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ----------------------------------------------------------------------- */
const TIERS = [
  { key: 'utilization_management', label: 'Utilization Management', tip: 'Always check here first — your own UM team may already have the records.' },
  { key: 'risk_adjustment', label: 'Risk Adjustment dept', tip: 'If UM doesn\'t have them, check your Risk Adjustment department next.' },
  { key: 'provider', label: 'Provider / facility', tip: 'Only if neither internal team has the records do we request them from the provider — via a secure upload portal.' },
];

function RecordsPanel({ caseId, requests, onChanged, setDraftModal }) {
  const [item, setItem] = useState('');
  const [deptId, setDeptId] = useState('');
  const [depts, setDepts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [draftBusy, setDraftBusy] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/departments`, { credentials: 'include' })
      .then((r) => r.json()).then((rows) => Array.isArray(rows) && setDepts(rows)).catch(() => {});
  }, []);

  const create = async () => {
    if (!item.trim()) return;
    setBusy(true);
    try {
      await api(`/cases/${caseId}/record-requests`, { method: 'POST', body: { requested_item: item, target_department_id: deptId || null } });
      setItem(''); setDeptId(''); onChanged();
    } catch (e) { alert(e.message); } finally { setBusy(false); }
  };
  const draftLetter = async () => {
    setDraftBusy(true);
    try {
      const { draft } = await api(`/cases/${caseId}/agents/draft`, { method: 'POST', body: { docType: 'records_request', extraContext: item ? { requested_item: item } : undefined } });
      setDraftModal({ title: 'Records request letter', draft });
    } catch (e) { alert(e.message); } finally { setDraftBusy(false); }
  };

  const update = async (id, status) => {
    try { await api(`/record-requests/${id}`, { method: 'PATCH', body: { status } }); onChanged(); }
    catch (e) { alert(e.message); }
  };
  const activeTier = requests?.find((r) => ['pending', 'sent'].includes(r.status))?.target_tier;

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5">
      <h2 className="font-semibold text-slate-800 flex items-center">Records — internal first, provider last<Hint text="The rule: before asking a provider, check internally — Utilization Management, then Risk Adjustment. Sentinel moves the request down the chain automatically when a team doesn't have the records." /></h2>

      {/* Cascade visual */}
      <div className="mt-3 flex items-center gap-2 text-xs">
        {TIERS.map((t, i) => (
          <span key={t.key} className="flex items-center gap-2">
            <Tooltip text={t.tip}>
              <span className={`px-2.5 py-1 rounded-full font-medium ${activeTier === t.key ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-500'}`}>{t.label}</span>
            </Tooltip>
            {i < TIERS.length - 1 && <span className="text-slate-300">→</span>}
          </span>
        ))}
      </div>

      {/* New request */}
      <div className="mt-4 space-y-2">
        <input value={item} onChange={(e) => setItem(e.target.value)} placeholder="What records do you need? e.g. claim history + chart for member X, 1/1–3/31"
          className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-200" />
        <div className="flex gap-2">
          <Tooltip text="Pick which internal team to ask first. That team's users get a request in their Sentinel inbox — they can provide the records without ever seeing the case. If they don't have them, the request moves to the next team, then the provider." width={300}>
            <select value={deptId} onChange={(e) => setDeptId(e.target.value)} className="px-3 py-2 rounded-xl border border-slate-200 text-sm bg-white">
              <option value="">Ask an internal team…</option>
              {depts.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </Tooltip>
          <Tooltip text="Generate a request-FOR-records letter you can send to an external provider/facility. This is an investigative request — distinct from a provider-education letter, which is an outcome.">
            <button onClick={draftLetter} disabled={draftBusy} className="px-3 py-2 rounded-xl bg-white border border-slate-300 text-slate-700 text-sm font-medium hover:bg-slate-50 inline-flex items-center gap-2 disabled:opacity-40">
              {draftBusy ? <span className="w-3.5 h-3.5 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin" /> : <AiGlyph className="w-3.5 h-3.5" />}
              Draft request letter
            </button>
          </Tooltip>
          <button onClick={create} disabled={busy} className="ml-auto px-4 py-2 rounded-xl bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-50">Send request</button>
        </div>
        <p className="text-[11px] text-slate-400">Works for claims too (e.g. "all claims for NPI X, telehealth, last 18 months"). Internal-first: ask Utilization Management or your Claims team, then Risk Adjustment, before the provider. For an external provider, use "Draft request letter" above, then upload their response under Evidence below.</p>
      </div>

      {/* Requests list */}
      <div className="mt-4 space-y-2">
        {(requests || []).map((r) => (
          <div key={r.id} className="flex items-center gap-3 p-3 rounded-xl border border-slate-100 text-sm">
            <div className="flex-1">
              <div className="text-slate-700">{r.requested_item}</div>
              <div className="text-xs text-slate-400 mt-0.5">
                {TIERS.find((t) => t.key === r.target_tier)?.label || r.target_tier}
                {r.rail === 'universe_request' && ' · secure provider portal'}
                {r.rail === 'workstream' && ' · internal request'}
              </div>
            </div>
            <Badge tone={r.status === 'fulfilled' ? 'green' : r.status === 'not_available' ? 'slate' : r.status === 'escalated' ? 'amber' : 'blue'}>{r.status}</Badge>
            {['pending', 'sent'].includes(r.status) && (
              <div className="flex gap-1">
                <Tooltip text="They provided the records."><button onClick={() => update(r.id, 'fulfilled')} className="px-2 py-1 rounded-lg bg-emerald-50 text-emerald-700 text-xs font-medium hover:bg-emerald-100">Got them</button></Tooltip>
                <Tooltip text="They don't have the records — Sentinel will move the request to the next team (and finally the provider)."><button onClick={() => update(r.id, 'not_available')} className="px-2 py-1 rounded-lg bg-amber-50 text-amber-700 text-xs font-medium hover:bg-amber-100">Don't have them →</button></Tooltip>
              </div>
            )}
          </div>
        ))}
        {(!requests || requests.length === 0) && <p className="text-sm text-slate-400 py-2">No records requested yet.</p>}
      </div>
    </div>
  );
}

/* ----------------------------------------------------------------------- */
function OverpaymentPanel({ caseId, overpayments, onChanged }) {
  const [form, setForm] = useState({ identified_date: '', amount_identified: '', basis: 'actual' });
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!form.identified_date || !form.amount_identified) { alert('Enter the date you identified it and the amount.'); return; }
    setBusy(true);
    try { await api(`/cases/${caseId}/overpayments`, { method: 'POST', body: { ...form, amount_identified: Number(form.amount_identified) } }); setForm({ identified_date: '', amount_identified: '', basis: 'actual' }); setOpen(false); onChanged(); }
    catch (e) { alert(e.message); } finally { setBusy(false); }
  };
  const markRecovered = async (id, amount) => { try { await api(`/overpayments/${id}`, { method: 'PATCH', body: { status: 'recovered', amount_recovered: amount } }); onChanged(); } catch (e) { alert(e.message); } };

  const clockTone = (due, status) => {
    if (['recovered', 'reported', 'written_off'].includes(status)) return 'green';
    const days = Math.ceil((new Date(due) - new Date()) / 86400000);
    return days <= 7 ? 'red' : days <= 21 ? 'amber' : 'slate';
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5">
      <div className="flex items-center">
        <h2 className="font-semibold text-slate-800 flex items-center">Overpayments &amp; the 60-day clock<Hint text="By law, an identified Medicare Advantage overpayment must be reported and returned within 60 days. Sentinel starts the clock the day you record it and warns you as the deadline nears." /></h2>
        <button onClick={() => setOpen(!open)} className="ml-auto text-sm text-red-600 font-medium hover:text-red-700">{open ? 'Cancel' : '+ Record an overpayment'}</button>
      </div>

      {open && (
        <div className="mt-3 grid grid-cols-3 gap-2 items-end">
          <div><div className="text-xs text-slate-500 mb-1">Date identified</div><input type="date" value={form.identified_date} onChange={(e) => setForm({ ...form, identified_date: e.target.value })} className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm" /></div>
          <div><div className="text-xs text-slate-500 mb-1">Amount</div><input type="number" value={form.amount_identified} onChange={(e) => setForm({ ...form, amount_identified: e.target.value })} className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm" placeholder="$" /></div>
          <div className="flex gap-2">
            <select value={form.basis} onChange={(e) => setForm({ ...form, basis: e.target.value })} className="px-2 py-2 rounded-xl border border-slate-200 text-sm flex-1">
              <option value="actual">Actual</option><option value="sample">Sample</option><option value="extrapolated">Extrapolated</option>
            </select>
            <button onClick={save} disabled={busy} className="px-3 py-2 rounded-xl bg-red-600 text-white text-sm font-semibold hover:bg-red-700">Save</button>
          </div>
        </div>
      )}

      <div className="mt-4 space-y-2">
        {(overpayments || []).map((o) => (
          <div key={o.id} className="flex items-center gap-3 p-3 rounded-xl border border-slate-100 text-sm">
            <div className="flex-1">
              <span className="font-semibold text-slate-800">{money(o.amount_identified)}</span>
              <span className="text-slate-400"> identified {dateShort(o.identified_date)} · {o.basis}</span>
              {Number(o.amount_recovered) > 0 && <span className="text-emerald-600"> · {money(o.amount_recovered)} recovered</span>}
            </div>
            <Tooltip text="The legal report-and-return deadline (identified date + 60 days).">
              <Badge tone={clockTone(o.report_due_at, o.status)}>due {dateShort(o.report_due_at)}</Badge>
            </Tooltip>
            {!['recovered', 'reported'].includes(o.status) && (
              <button onClick={() => markRecovered(o.id, o.amount_identified)} className="px-2 py-1 rounded-lg bg-emerald-50 text-emerald-700 text-xs font-medium hover:bg-emerald-100">Mark recovered</button>
            )}
          </div>
        ))}
        {(!overpayments || overpayments.length === 0) && <p className="text-sm text-slate-400 py-2">No overpayments recorded.</p>}
      </div>
    </div>
  );
}

/* ----------------------------------------------------------------------- */
function ReferralPanel({ caseId, referrals, onChanged }) {
  const [busy, setBusy] = useState(false);
  const build = async () => {
    setBusy(true);
    try { await api(`/cases/${caseId}/referrals`, { method: 'POST', body: { target: 'imedic' } }); onChanged(); }
    catch (e) { alert(e.message); } finally { setBusy(false); }
  };
  const markSubmitted = async (id) => {
    const ext = window.prompt('I-MEDIC case number (if returned):') || null;
    try { await api(`/referrals/${id}`, { method: 'PATCH', body: { status: 'submitted', external_case_number: ext } }); onChanged(); } catch (e) { alert(e.message); }
  };
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5">
      <div className="flex items-center">
        <h2 className="font-semibold text-slate-800 flex items-center">Refer to the government<Hint text="When a case is real fraud, refer it to the I-MEDIC (Qlarant). Sentinel pre-fills the official complaint-form fields from this case — you review, then submit." /></h2>
        <button onClick={build} disabled={busy} className="ml-auto px-3 py-1.5 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 disabled:opacity-40">{busy ? 'Building…' : 'Build I-MEDIC referral'}</button>
      </div>
      <div className="mt-3 space-y-2">
        {(referrals || []).map((r) => (
          <div key={r.id} className="flex items-center gap-3 p-3 rounded-xl border border-slate-100 text-sm">
            <span className="font-medium text-slate-700 uppercase text-xs">{r.target}</span>
            <Badge tone={r.status === 'submitted' ? 'green' : 'blue'}>{r.status}</Badge>
            {r.external_case_number && <span className="text-xs text-slate-400 font-mono">#{r.external_case_number}</span>}
            <div className="ml-auto">
              {r.status !== 'submitted' && <button onClick={() => markSubmitted(r.id)} className="px-2 py-1 rounded-lg bg-emerald-50 text-emerald-700 text-xs font-medium hover:bg-emerald-100">Mark submitted</button>}
            </div>
          </div>
        ))}
        {(!referrals || referrals.length === 0) && <p className="text-sm text-slate-400 py-2">No referrals yet.</p>}
      </div>
    </div>
  );
}

/* ----------------------------------------------------------------------- */
function Timeline({ caseId, events, onChanged }) {
  const [note, setNote] = useState('');
  const add = async () => {
    if (!note.trim()) return;
    try { await api(`/cases/${caseId}/events`, { method: 'POST', body: { summary: note } }); setNote(''); onChanged(); }
    catch (e) { alert(e.message); }
  };
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5">
      <h2 className="font-semibold text-slate-800 flex items-center">Activity &amp; audit trail<Hint text="A permanent, tamper-proof record of everything that happened on this case — the backbone of an audit-defensible file." /></h2>
      <div className="mt-3 flex gap-2">
        <input value={note} onChange={(e) => setNote(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && add()} placeholder="Add a note (a call made, a record received, a decision)…"
          className="flex-1 px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-200" />
        <button onClick={add} className="px-4 py-2 rounded-xl bg-slate-900 text-white text-sm font-medium hover:bg-slate-800">Add note</button>
      </div>
      <div className="mt-4 space-y-3">
        {(events || []).map((e) => (
          <div key={e.id} className="flex gap-3 text-sm">
            <div className="w-1.5 h-1.5 rounded-full bg-slate-300 mt-1.5 flex-shrink-0" />
            <div>
              <p className="text-slate-700">{e.summary || e.event_type}</p>
              <p className="text-xs text-slate-400">{dateTime(e.created_at)}</p>
            </div>
          </div>
        ))}
        {(!events || events.length === 0) && <p className="text-sm text-slate-400">No activity yet.</p>}
      </div>
    </div>
  );
}

/* ----------------------------------------------------------------------- */
// Evidence vault — upload records received (e.g., a chart emailed back), download,
// and analyze with AI. Files are stored encrypted at rest.
function EvidencePanel({ caseId, evidence, onChanged, setDraftModal }) {
  const [busy, setBusy] = useState(false);
  const [analyzingId, setAnalyzingId] = useState(null);

  const onUpload = async (e) => {
    const file = e.target.files?.[0]; e.target.value = '';
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await fetch(`${API}/api/sentinel/cases/${caseId}/evidence`, { method: 'POST', credentials: 'include', body: fd });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json.success === false) throw new Error(json.message || 'Upload failed.');
      onChanged();
    } catch (e2) { alert(e2.message); } finally { setBusy(false); }
  };

  const analyze = async (ev) => {
    setAnalyzingId(ev.id);
    try {
      const res = await fetch(`${API}/api/sentinel/evidence/${ev.id}/analyze`, { method: 'POST', credentials: 'include' });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json.success === false) throw new Error(json.message || 'Could not analyze.');
      const a = json.analysis || {};
      const body = [
        a.summary || '',
        a.key_findings?.length ? '\nKey findings:\n- ' + a.key_findings.join('\n- ') : '',
        a.relevance_to_case ? '\nRelevance to the case:\n' + a.relevance_to_case : '',
        a.red_flags?.length ? '\nRed flags:\n- ' + a.red_flags.join('\n- ') : '',
      ].filter(Boolean).join('\n');
      setDraftModal({ title: `AI analysis — ${ev.original_name || ev.title}`, draft: { subject: '', body } });
    } catch (e2) { alert(e2.message); } finally { setAnalyzingId(null); }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5">
      <div className="flex items-center">
        <h2 className="font-semibold text-slate-800 flex items-center">Evidence &amp; records<Hint text="The case file. Upload records you receive during the investigation — a chart emailed back by a provider, claims pulled by your Claims team, a statement. Each file is stored encrypted, and the AI can read and summarize it for you." /></h2>
        <label className="ml-auto px-3 py-1.5 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700 cursor-pointer inline-flex items-center gap-2">
          <input type="file" className="hidden" onChange={onUpload} accept=".pdf,.docx,.doc,.txt,.eml,.msg,.rtf,.csv,.html,.htm,.png,.jpg,.jpeg,.tif,.tiff" />
          {busy ? 'Uploading…' : 'Upload records'}
        </label>
      </div>
      <PhiFriendlyNote variant="minimum-necessary" className="mt-3" />
      <div className="mt-3 space-y-2">
        {(evidence || []).map((ev) => (
          <div key={ev.id} className="flex items-center gap-3 p-3 rounded-xl border border-slate-100 text-sm">
            <div className="flex-1">
              <div className="text-slate-700">{ev.original_name || ev.title}</div>
              <div className="text-xs text-slate-400">{ev.source_kind || ev.kind} · {dateTime(ev.created_at)}</div>
            </div>
            {ev.has_file && <a href={`${API}/api/sentinel/evidence/${ev.id}/download`} className="px-2 py-1 rounded-lg bg-slate-100 text-slate-700 text-xs font-medium hover:bg-slate-200">Download</a>}
            {ev.has_file && (
              <button onClick={() => analyze(ev)} disabled={analyzingId === ev.id} className="px-2 py-1 rounded-lg bg-slate-900 text-white text-xs font-medium hover:bg-slate-800 inline-flex items-center gap-1.5 disabled:opacity-40">
                {analyzingId === ev.id ? <span className="w-3 h-3 border-2 border-white/40 border-t-white rounded-full animate-spin" /> : <AiGlyph className="w-3.5 h-3.5" />}
                Analyze
              </button>
            )}
          </div>
        ))}
        {(!evidence || evidence.length === 0) && <p className="text-sm text-slate-400 py-2">No documents yet. Upload the records you receive — or attach the source document at intake.</p>}
      </div>
    </div>
  );
}

/* ----------------------------------------------------------------------- */
// #1 Shared subject memory — what this plan already knows about the NPI.
function SubjectMemoryStrip({ memory }) {
  if (!memory || !memory.prior_cases) return null;
  return (
    <div className="mt-4 rounded-xl bg-amber-50 border border-amber-100 px-4 py-3">
      <div className="text-xs font-semibold text-amber-800 flex items-center">
        Subject history at this plan
        <Hint text="What Sentinel already knows about this provider across all prior cases. It's shared with the AI agents automatically, so a repeat subject is weighed with the right level of concern." />
      </div>
      <p className="text-sm text-amber-900 mt-1">
        {memory.prior_cases} prior case{memory.prior_cases === 1 ? '' : 's'}
        {memory.confirmed_cases ? ` · ${memory.confirmed_cases} previously confirmed` : ''}
        {memory.recovered_total ? ` · ${money(memory.recovered_total)} recovered` : ''}
        {memory.ever_excluded ? ' · previously on an exclusion list' : ''}.
      </p>
    </div>
  );
}

/* ----------------------------------------------------------------------- */
// #2 Resolve with a final finding — closes the outcome-learning loop.
function ResolvePanel({ caseId, onChanged }) {
  const [open, setOpen] = useState(false);
  const [finding, setFinding] = useState('substantiated');
  const [summary, setSummary] = useState('');
  const [busy, setBusy] = useState(false);
  const FINDINGS = [
    { v: 'substantiated', label: 'Substantiated — fraud/abuse confirmed' },
    { v: 'partially_substantiated', label: 'Partially substantiated' },
    { v: 'unsubstantiated', label: 'Unsubstantiated — no FWA found' },
    { v: 'unable_to_determine', label: 'Unable to determine' },
  ];
  const resolve = async () => {
    setBusy(true);
    try { await api(`/cases/${caseId}/resolve`, { method: 'POST', body: { finding, closure_summary: summary } }); onChanged(); }
    catch (e) { alert(e.message); } finally { setBusy(false); }
  };
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5">
      <div className="flex items-center">
        <h2 className="font-semibold text-slate-800 flex items-center">Resolve &amp; close<Hint text="Record how the case actually turned out. Your finding teaches the Triage agent whether its original call held up — closing the learning loop for the whole team." /></h2>
        <button onClick={() => setOpen(!open)} className="ml-auto text-sm text-red-600 font-medium hover:text-red-700">{open ? 'Cancel' : 'Resolve case'}</button>
      </div>
      {open && (
        <div className="mt-3 space-y-3">
          <div>
            <div className="text-xs text-slate-500 mb-1">Final finding</div>
            <select value={finding} onChange={(e) => setFinding(e.target.value)} className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm bg-white">
              {FINDINGS.map((f) => <option key={f.v} value={f.v}>{f.label}</option>)}
            </select>
          </div>
          <textarea value={summary} onChange={(e) => setSummary(e.target.value)} rows={2} placeholder="Closure summary (optional)…" className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm resize-none" />
          <div className="flex justify-end">
            <button onClick={resolve} disabled={busy} className="px-4 py-2 rounded-xl bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-50">{busy ? 'Closing…' : 'Close case'}</button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ----------------------------------------------------------------------- */
function DraftModal({ title, draft, onClose }) {
  const [copied, setCopied] = useState(false);
  const text = `${draft?.subject ? draft.subject + '\n\n' : ''}${draft?.body || ''}`;
  const copy = () => { navigator.clipboard?.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500); };
  return (
    <div className="fixed inset-0 bg-slate-900/40 flex items-center justify-center z-[10000] p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-100 flex items-center">
          <h3 className="font-semibold text-slate-800">{title}</h3>
          <span className="ml-2 text-xs text-slate-400">AI draft — review &amp; edit before sending</span>
          <button onClick={onClose} className="ml-auto text-slate-400 hover:text-slate-700">✕</button>
        </div>
        <div className="p-5">
          {draft?.subject && <p className="font-medium text-slate-800 mb-2">{draft.subject}</p>}
          <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{draft?.body}</p>
        </div>
        <div className="px-5 py-3 border-t border-slate-100 flex justify-end gap-2">
          <button onClick={copy} className="px-3 py-1.5 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800">{copied ? 'Copied ✓' : 'Copy text'}</button>
        </div>
      </div>
    </div>
  );
}
